import _round from 'lodash/round';

import { getNumberFormat } from './getNumberFormat';

const smallPrecision = 2;

const smallFormatter = getNumberFormat('en', {
    compactDisplay: 'short',
    notation: 'compact',
    maximumFractionDigits: smallPrecision,
    minimumFractionDigits: smallPrecision,
});

const largePrecision = 1;

const largeFormatter = getNumberFormat('en', {
    // can't use 'currency' option here because it doesn't allow maximumFractionDigits below 2
    compactDisplay: 'short',
    notation: 'compact',
    maximumFractionDigits: largePrecision,
});

const zero = '$0.00';

/**
 * Formats a number using Intl.NumberFormat. For numbers greater than -1000 and less than 1000, up
 * to two decimal places are used. For numbers less than or equal to -1000, or greater than or equal
 * to 1000, a roman numeral (such as K for "thousand") is appended and up to one decimal place is
 * used.
 *
 * Examples:
 *      formatDollarsCompact(.01) =>  $0.01
 *      formatDollarsCompact(999.01) =>  $999.01
 *      formatDollarsCompact(1110.01) =>  $1.1K
 *      formatDollarsCompact(203013.01) =>  $203K
 *      formatDollarsCompact(1324683212.12) =>  $1.3B
 * @param param
 */
export const formatDollarsCompact = (value: Maybe<number>): string => {
    if (value === null) {
        return zero;
    }

    if (value > -1000 && value < 1000) {
        const smallRounded = _round(value, smallPrecision);

        if (smallRounded === 0) {
            return zero;
        }

        if (value < 0) {
            return `-$${smallFormatter.format(Math.abs(value))}`;
        }

        if (value < 1000) {
            return `$${smallFormatter.format(value)}`;
        }
    }

    const largeRounded = _round(value, largePrecision);

    if (largeRounded === 0) {
        return zero;
    }

    if (value <= -1000) {
        return `-$${largeFormatter.format(Math.abs(value))}`;
    }

    return `$${largeFormatter.format(value)}`;
};
